import { useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api-client";
import { Icon } from "../ui/icon";

// Real account-filter dropdown, wired to GET/POST /api/v2/account-filter
// (the ONE global, cookie-backed filter every screen already reads server-side
// — see api-client.ts's own comment on it). The reference mockups also show
// per-account "LoggedIn" status chips here, sourced from server.ts's
// getIngestHealth()/accountStatusChips() — there is no JSON route exposing
// that yet, so this omits the chips rather than fabricating fake statuses;
// a follow-up can add GET /api/v2/account-health once that's needed.
interface TopbarProps {
  onMenuClick: () => void;
  railCollapsed: boolean;
}

export function Topbar({ onMenuClick, railCollapsed }: TopbarProps) {
  const [state, setState] = useState<{ options: { id: string; name: string }[]; selected: string | null } | null>(
    null
  );
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // null while loading; undefined would also mean "not shown yet", but the
  // distinct name (`| null`) matches every other field on this page — never
  // set once getMe() resolves, whether or not it has a real name (workos)
  // or not (every other CONSOLE_AUTH_MODE — see MeResponse's own comment).
  const [me, setMe] = useState<{ name: string | null; email: string | null } | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getAccountFilter()
      .then((s) => {
        if (!cancelled) setState(s);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : "Could not load account filter.");
      });
    // Best-effort: a failure here (e.g. non-workos auth mode, or a stale
    // 401 mid-navigation) just leaves the person icon unlabeled rather than
    // surfacing an error banner over the account filter's own.
    api
      .getMe()
      .then((m) => {
        if (!cancelled) setMe({ name: m.name, email: m.email });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  async function apply(accountId: string | null) {
    setPending(true);
    setError(null);
    try {
      const next = await api.setAccountFilter(accountId);
      setState(next);
      // The account filter is global (a cookie every screen reads
      // server-side) — reload so every already-fetched screen re-queries
      // under the new filter rather than silently going stale.
      window.location.reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not update account filter.");
    } finally {
      setPending(false);
    }
  }

  return (
    <header className={`fixed top-0 left-0 ${railCollapsed ? "md:left-16" : "md:left-64"} right-0 h-14 bg-surface-container-lowest/95 backdrop-blur-md border-b border-outline-variant/40 z-40 px-spacing-lg flex items-center justify-between gap-spacing-base transition-[left] duration-200 ease-out`}>
      <div className="flex items-center gap-spacing-lg overflow-x-auto py-1">
        <button
          type="button"
          onClick={onMenuClick}
          aria-label="Toggle navigation menu"
          className="md:hidden shrink-0 w-8 h-8 flex items-center justify-center rounded-xl text-on-surface-variant hover:bg-surface-container hover:text-on-surface transition-colors"
        >
          <Icon name="menu" className="text-[20px]" />
        </button>
        <div className="flex items-center gap-spacing-xs shrink-0">
          <span className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider">
            Account:
          </span>
          <div className="bg-surface-container-low border border-outline-variant/40 px-spacing-sm py-spacing-3xs rounded-xl">
            <select
              aria-label="LinkedIn account filter"
              className="bg-transparent font-body-sm text-body-sm text-on-surface focus:outline-none cursor-pointer"
              value={state?.selected ?? ""}
              disabled={!state || pending}
              onChange={(e) => apply(e.target.value || null)}
            >
              <option value="">All LinkedIn accounts</option>
              {state?.options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
          {state?.selected && (
            <button
              type="button"
              className="font-label-sm text-label-sm text-primary hover:text-secondary transition-colors px-spacing-xs py-spacing-3xs disabled:opacity-50"
              disabled={pending}
              onClick={() => apply(null)}
            >
              Reset
            </button>
          )}
        </div>
        {error && <span className="font-label-sm text-label-sm text-error shrink-0">{error}</span>}
      </div>
      <div className="flex items-center gap-spacing-sm shrink-0">
        {(me?.name || me?.email) && (
          <span className="font-label-sm text-label-sm text-on-surface-variant max-w-[12rem] truncate" title={me?.email ?? undefined}>
            {me.name ?? me.email}
          </span>
        )}
        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0" title={me?.email ?? undefined}>
          <Icon name="person" className="text-on-primary text-[18px]" />
        </div>
      </div>
    </header>
  );
}
